import { useCallback, useMemo, useState } from 'react';
import { Box, ButtonBase, InputBase, makeStyles, Popper } from '@material-ui/core';
import _ from 'lodash';
import { EditorState } from 'draft-js';
import { AutoCompleteSelect, RichText } from '../../../../components';
import Dialog from '../../../../components/Dialog';
import { AddIcon, DropDownIcon, DropUpIcon } from '../../../../components/Icon';
import LongRemark from '../../../../components/LongRemark';
import EmptyBed from '../Icons/EmptyBed';
import {
  BED_STATUS_REMARK_COLOR,
  NEW_BED_STATUS_CUBICLE_ITEM_LIST,
  NEW_BED_STATUS_LENGTH,
} from '../../../../comm/constant';
import AutoCompleteInput from '../../../../components/AutoCompleteInput';
import { editorStateInsertText, generateGrid, initColor } from '../../../../utils/newBedStatusUtil';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import { Checkbox } from '../../../DutyAssign/components/Attendance/Setting';
import StyleButton from '../../../../components/StyleButton';
import { initValue } from '../../../../utils';
import CubicleRemarksPopup from './CubicleRemarksPopup';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
  },
  star: {
    color: '#ff0000',
  },
  input: {
    width: '100%',
    height: '3.5rem',
    padding: '1.25rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  remarkInput: {
    width: '100%',
    height: '12.875rem',
    padding: '1.25rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    alignItems: 'flex-start',
    '& .MuiInputBase-inputMultiline': {
      height: '100% !important',
      overflow: 'auto !important',
    },
  },
  titleRemark: {
    color: '#c0c6cb',
    marginLeft: '0.2rem',
  },
  addRemarkBtn: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#538fff',
  },
  addIcon: {
    fontSize: '1.2rem',
    marginRight: '0.45rem',
  },
  border: {
    width: '0.063rem',
    height: '1.188rem',
    backgroundColor: '#e4e7eb',
  },
  palette: ({ form }) => ({
    width: '1.375rem',
    height: '1.375rem',
    minWidth: '1.375rem',
    minHeight: '1.375rem',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    marginRight: '0.75rem',
    backgroundColor: initValue(BED_STATUS_REMARK_COLOR[form.remarkColor]?.bgColor, 'fff'),
  }),
  dropDownIcon: {
    color: '#538fff',
    fontSize: '0.75rem',
  },
  remarkColorPopup: {
    zIndex: '9999',
  },
  colourItem: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    width: '100%',
    height: '2.875rem',
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: '1.563rem',
    '&:hover': {
      backgroundColor: '#f5faff',
    },
  },
  bedArrangement: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: '#538fff',
  },
  deletePopupContent: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.25rem',
    color: '#2e4469',
  },
  content: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
}));

// edit mode 里面修改 memo 的 popup
const EditMemo = ({ edit, handleClose, data, handleDeleteCubicle, handleEditCubicle, handleCopyCubicle }) => {
  const [remarkAnchorEl, setRemarkAnchorEl] = useState(null);
  const [remarkPopupAnchorEl, setRemarkPopupAnchorEl] = useState(null);
  const [form, setForm] = useState({
    name: data.name ?? '',
    type: data.type ?? '',
    status: data.status ?? '',
    gender: data.gender ?? '',
    color: data.color ?? '',
    remarks: data.remarks ?? EditorState.createEmpty(),
    remarkColor: data.remarkColor ?? '',
    bedArrangement: data.bedArrangement ?? '',
    borderLine: data.borderLine,
  });
  const classes = useStyles({ form });

  const updateForm = useCallback(
    (type) => (e) => {
      switch (type) {
        case 'name':
          setForm((o) => ({ ...o, [type]: e.target.value }));
          break;
        case 'remarks':
        case 'type':
        case 'status':
        case 'gender':
        case 'color':
          setForm((o) => ({ ...o, [type]: e }));
          break;
        case 'remarkColor':
          return () => {
            setRemarkAnchorEl(null);
            setForm((o) => ({ ...o, [type]: e.value }));
          };
        default:
          break;
      }
    },
    []
  );

  const handleRemarkOpen = useCallback(
    (event) => {
      if (remarkAnchorEl) {
        setRemarkAnchorEl(null);
      } else {
        setRemarkAnchorEl(event.currentTarget);
      }
    },
    [remarkAnchorEl]
  );

  // 选择床位
  const handleBedArrangement = useCallback(
    ({ column, row }) =>
      () => {
        setForm((o) => {
          let maxRow = 0;
          let maxColumn = 0;

          data.beds.forEach((item) => {
            if (item.data && item.row > maxRow) {
              maxRow = item.row;
            }
            if (item.data && item.column > maxColumn) {
              maxColumn = item.column;
            }
          });

          if (column + 1 < maxColumn || row + 1 < maxRow) {
            return o;
          } else {
            o.bedArrangement = { column: column + 1, row: row + 1 };
            return { ...o };
          }
        });
      },
    [data.beds]
  );

  // 判断床位是否选择上
  const handleSelected = useCallback(
    ({ column, row }) => {
      return Boolean(form.bedArrangement.column >= column + 1 && form.bedArrangement.row >= row + 1);
    },
    [form.bedArrangement]
  );

  // 提交修改
  const handleSubmit = useCallback(() => {
    handleEditCubicle({
      id: data.id,
      data: {
        ...form,
        beds: _.map(generateGrid(form.bedArrangement.row, form.bedArrangement.column), (item) => {
          const findBedItem = _.find(
            data.beds,
            (dataBedItem) => dataBedItem.row === item.row && dataBedItem.column === item.column
          );

          if (findBedItem?.data) {
            return findBedItem;
          } else {
            return item;
          }
        }),
        version: data.version ?? null,
      },
    });
    handleClose();
  }, [handleEditCubicle, form, data, handleClose]);

  // 没填写必填的时候禁用
  const confirmDisable = useMemo(() => {
    return !form.name;
  }, [form.name]);

  // 选择性别的时候，自动切换默认颜色
  useUpdateEffect(() => {
    setForm((o) => ({
      ...o,
      color: initColor(o.gender),
    }));
  }, [form.gender]);

  // remark popup 开关
  const handleRemarkPopup = useCallback(
    (event) => {
      setRemarkPopupAnchorEl(remarkPopupAnchorEl ? null : event.currentTarget);
    },
    [remarkPopupAnchorEl]
  );

  // 关闭remark popup
  const handleCloseRemarkPopup = useCallback(() => {
    setRemarkPopupAnchorEl(null);
  }, []);

  // 选择remark
  const handleSelectRemark = useCallback(
    (remark, color) => () => {
      setForm((o) => ({
        ...o,
        remarks: editorStateInsertText(o.remarks, remark),
        remarkColor: color,
      }));

      setRemarkPopupAnchorEl(null);
    },
    []
  );

  // 选择是否显示border
  const handleSelectBorderLine = useCallback(() => {
    setForm((o) => ({
      ...o,
      borderLine: o.borderLine === 'show' ? 'hide' : 'show',
    }));
  }, []);

  // 处理 copy cubicle
  const copy = useCallback(() => {
    handleCopyCubicle({
      data: {
        ...form,
        beds: generateGrid(form.bedArrangement.row, form.bedArrangement.column),
        name: `${form.name}(copy)`,
      },
    });
    handleClose();
  }, [handleCopyCubicle]);

  return (
    <>
      <Dialog open={true} width="80vw" title="Edit" handleClose={handleClose} haveFoot={false}>
        <Box display="flex" gridGap="2.5rem">
          <Box display="flex" flexDirection="column" gridGap="1.25rem" flexBasis="60%">
            {/* 第一行 */}
            <Box display="flex" gridGap="1.25rem">
              <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="100%">
                <div className={classes.title}>
                  Name<span className={classes.star}>*</span>
                  <span className={classes.titleRemark}>(20 words limited) </span>
                </div>
                <InputBase
                  className={classes.input}
                  placeholder="Cubicle Name"
                  inputProps={{ maxLength: NEW_BED_STATUS_LENGTH.CUBICLE_NAME }}
                  value={form.name}
                  onChange={updateForm('name')}
                />
              </Box>
            </Box>
            {/* 第二行 */}
            <Box display="flex" gridGap="1.25rem">
              <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                <Box display="flex">
                  <div className={classes.title}> Status </div>
                  <LongRemark
                    content="• You can select the status in the list or just leave it blank.
• If there is no suitable status in the list, kindly input by free text.
"
                  />
                </Box>
                <AutoCompleteInput
                  width="100%"
                  placeholder="Select Status"
                  itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleStatus}
                  value={form.status}
                  freeSolo
                  onChange={updateForm('status')}
                  maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_STATUS}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                <div className={classes.title}> Colour </div>
                <AutoCompleteSelect
                  type="color"
                  width="100%"
                  placeholder="Select Colour"
                  itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleColor}
                  value={form.color}
                  onChange={updateForm('color')}
                  colorIconBorder="solid 1px #e4e7eb"
                />
                <Box display="flex" alignItems="center" gridGap="0.75rem">
                  <Checkbox check={form.borderLine === 'show'} handleChange={handleSelectBorderLine} />
                  <div className={classes.content}>Show border line</div>
                </Box>
              </Box>
            </Box>
            {/* 第四行 */}
            <Box display="flex" gridGap="1.25rem">
              <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="100%">
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <div className={classes.title}>
                    Remarks
                    <span className={classes.titleRemark}>(1000 words limited) </span>
                  </div>
                  <Box display="flex" alignItems="center" gridGap="0.969rem">
                    <ButtonBase className={classes.addRemarkBtn} onClick={handleRemarkPopup}>
                      <AddIcon className={classes.addIcon} />
                      Add Remarks
                    </ButtonBase>
                    <div className={classes.border} />
                    <ButtonBase onClick={handleRemarkOpen}>
                      <div className={classes.palette} />
                      {remarkAnchorEl ? (
                        <DropUpIcon className={classes.dropDownIcon} />
                      ) : (
                        <DropDownIcon className={classes.dropDownIcon} />
                      )}
                    </ButtonBase>
                  </Box>
                </Box>
                <RichText value={form.remarks} onChange={updateForm('remarks')} />
              </Box>
            </Box>
          </Box>
          <div className={classes.border} style={{ height: '46rem' }} />
          {/* 第二列 */}
          <Box display="flex" flexDirection="column" gridGap="1rem" flexBasis="40%">
            <Box display="flex">
              <div className={classes.title}>Size</div>
              <LongRemark content="Size" />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              padding="0.625rem 1rem 1.313rem"
              bgcolor="#f1f5fb"
              borderRadius="8px"
              gridGap="0.75rem"
            >
              <div className={classes.bedArrangement}>{`${form.bedArrangement.column}x${form.bedArrangement.row}`}</div>
              <Box
                display="grid"
                gridGap="0.45rem"
                gridTemplateColumns="1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr"
              >
                {bedArray.map((item) => (
                  <ButtonBase onClick={handleBedArrangement(item)} key={JSON.stringify(item)}>
                    <EmptyBed selected={handleSelected(item)} popup />
                  </ButtonBase>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box display="flex" justifyContent="center" mt="3.25rem" gridGap="1.25rem">
          <StyleButton onClick={copy} text="Copy" type="fill" width="12.5rem" backgroundColor="#28d2ab" />
          <StyleButton
            onClick={handleDeleteCubicle(data.id)}
            text="Delete"
            type="fill"
            width="12.5rem"
            backgroundColor="#ff655c"
          />
          <StyleButton onClick={handleSubmit} text="Confirm" type="fill" width="12.5rem" disabled={confirmDisable} />
        </Box>
      </Dialog>

      {/* remark color popup */}
      <Popper
        className={classes.remarkColorPopup}
        open={Boolean(remarkAnchorEl)}
        anchorEl={remarkAnchorEl}
        placement="bottom-end"
        transition
      >
        <Box
          display="flex"
          flexDirection="column"
          width="10rem"
          border="solid 1px #e4e7eb"
          bgcolor="#fff"
          boxShadow="0 1px 6px 0 rgba(0, 0, 0, 0.12)"
          borderRadius="10px"
          padding="0.5rem 0"
        >
          {NEW_BED_STATUS_CUBICLE_ITEM_LIST.RemarkColor.map((item) => (
            <ButtonBase className={classes.colourItem} key={item.value} onClick={updateForm('remarkColor')(item)}>
              <div className={classes.palette} style={{ backgroundColor: item.color }} /> {item.text}
            </ButtonBase>
          ))}
        </Box>
      </Popper>

      {/* remark popup */}
      <CubicleRemarksPopup
        anchorEl={remarkPopupAnchorEl}
        handleClose={handleCloseRemarkPopup}
        handleClick={handleSelectRemark}
      />
    </>
  );
};

const rows = 12;
const cols = 14;

const bedArray = Array.from({ length: rows * cols }, (_, index) => ({
  column: index % cols,
  row: Math.floor(index / cols),
}));

export default EditMemo;
